import autocompleteTemplate from './autocomplete.html?raw';
import { MessageFormatter } from '../../utils/message-formatter';
import { getCurrentLocale } from '../../utils/index.js';

export class Autocomplete extends HTMLElement {
    static get observedAttributes() {
        return [
            'label',
            'disabled',
            'placeholder',
            'options',
            'no-results-text',
            'num-results-text',
            'max-items',
            'min-chars',
            'option-selected-text',
            'describedby-text',
            'min-chars-text',
            'select-on-list-tab',
        ];
    }

    constructor() {
        super();

        this.settings = {
            customFilter: null,
            label: '',
            labelField: 'label',
            valueField: 'value',
            filterFields: ['label', 'value'],
            minChars: 2,
            maxItems: 10,
            inputDelay: 500,
            localization: {
                noResults: 'No results found',
                numResults:
                    '{count, plural, one {# result} other {# results}} found',
                itemSelected: '{option} selected',
                describedBy:
                    'When autocomplete results are available use the up and down arrow keys to navigate the list. Press enter to select an option.',
                minCharsHint:
                    'Type at least {minCount} characters to see results.',
            },
            selectOnListTab: false,
            placeholder: ' ',
            options: [],
            showNoOptionsFound: true,
            onInput: null,
            onSelect: null,
            render: null,
        };

        this.selectedOption = null;
        this.filteredOptions = [];
        this.inputTimer = null;
        this.lastMessage = null;
        this.formatter = null;
    }

    connectedCallback() {
        this.attachShadow({ mode: 'open' });

        const template = document.createElement('template');
        template.innerHTML = autocompleteTemplate;
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        this.settings.label = this.getAttribute('label');
        this.settings.placeholder = this.getAttribute('placeholder') ?? ' ';

        const input = this.shadowRoot.querySelector('input');
        const label = this.shadowRoot.querySelector('label');

        if (input) {
            input.setAttribute('placeholder', this.settings.placeholder);

            if (this.isDisabled) {
                input.setAttribute('disabled', '');
            }
        }

        if (label) {
            if (this.settings.placeholder === ' ') {
                label.textContent = this.settings.label;

                if (!label.classList.contains('floating-label')) {
                    label.classList.add('floating-label');
                }

                label.classList.remove('hidden');
            } else {
                label.classList.remove('floating-label');

                if (!label.classList.contains('hidden')) {
                    label.classList.add('hidden');
                }

                if (input) {
                    input.setAttribute(
                        'aria-label',
                        this.settings.label ?? this.settings.placeholder
                    );
                }
            }

            if (this.hasAttribute('max-items')) {
                const maxItems = parseInt(this.getAttribute('max-items'), 10);

                if (!isNaN(maxItems) && maxItems > 0) {
                    this.settings.maxItems = maxItems;
                }
            }

            if (this.hasAttribute('min-chars')) {
                const minChars = parseInt(this.getAttribute('min-chars'), 10);
                if (!isNaN(minChars) && minChars > 0) {
                    this.settings.minChars = minChars;
                }
            }

            if (this.hasAttribute('select-on-list-tab')) {
                const selectOnListTab =
                    this.getAttribute('select-on-list-tab') !== 'false';

                this.settings.selectOnListTab = selectOnListTab;
            }
        }

        if (this.hasAttribute('options')) {
            const tmp = JSON.parse(this.getAttribute('options'));

            if (Array.isArray(tmp)) {
                const isArrayofStrings = tmp.every(
                    (item) => typeof item === 'string'
                );

                if (isArrayofStrings) {
                    this.settings.options = tmp.map((item) => ({
                        label: item,
                        value: item,
                    }));
                } else {
                    this.settings.options = tmp;
                }
            } else {
                console.warn('The options attribute must be a JSON array.');
            }
        }

        if (this.hasAttribute('no-results-text')) {
            this.settings.localization.noResults =
                this.getAttribute('no-results-text');
        }

        if (this.hasAttribute('num-results-text')) {
            this.settings.localization.numResults =
                this.getAttribute('num-results-text');
        }

        if (this.hasAttribute('option-selected-text')) {
            this.settings.localization.optionSelected = this.getAttribute(
                'option-selected-text'
            );
        }

        if (this.hasAttribute('min-chars-text')) {
            this.settings.localization.minCharsHint =
                this.getAttribute('min-chars-text');
        }

        if (this.hasAttribute('describedby-text')) {
            const describedByText = this.getAttribute('describedby-text');
            this.settings.localization.describedBy = describedByText;

            const assistiveLabel = this.shadowRoot.getElementById(
                'autocomplete-assistive-label'
            );

            if (assistiveLabel) {
                assistiveLabel.textContent = describedByText;
            }
        }

        this.messageFormatter = new MessageFormatter(getCurrentLocale());
        this.addEventListeners();
    }

    attributeChangedCallback(name, _, newValue) {
        const shadowRoot = this.shadowRoot;

        if (!shadowRoot) return;

        const input = shadowRoot.querySelector('input');

        switch (name) {
            case 'label':
                this.settings.label = newValue;
                this.updateFloatingLabelClasses();
                break;
            case 'disabled':
                if (input) {
                    input.disabled = newValue !== 'false';
                }
                break;
            case 'placeholder':
                this.settings.placeholder = newValue ?? ' ';
                if (input) {
                    input.placeholder = this.settings.placeholder;
                }
                this.updateFloatingLabelClasses();
                break;
            case 'options':
                if (newValue) {
                    const tmp = JSON.parse(newValue);
                    if (Array.isArray(tmp)) {
                        const isArrayofStrings = tmp.every(
                            (item) => typeof item === 'string'
                        );
                        if (isArrayofStrings) {
                            this.settings.options = tmp.map((item) => ({
                                label: item,
                                value: item,
                            }));
                        } else {
                            this.settings.options = tmp;
                        }
                    } else {
                        console.warn(
                            'The options attribute must be a JSON array.'
                        );
                    }
                } else {
                    this.settings.options = [];
                }
                break;
            case 'no-results-text': {
                this.settings.localization.noResults = newValue;
                const noResultsItem = this.shadowRoot.querySelector(
                    '.autocomplete-item.no-results'
                );

                if (noResultsItem && this.settings.localization.noResults) {
                    noResultsItem.textContent = noResultsItem.textContent =
                        this.messageFormatter(
                            this.settings.localization.noResults,
                            {
                                input: input.value,
                            }
                        );
                }
                break;
            }
            case 'num-results-text': {
                this.settings.localization.numResults = newValue;
                break;
            }
            case 'option-selected-text': {
                this.settings.localization.itemSelected = newValue;
                break;
            }
            case 'describedby-text': {
                this.settings.localization.describedBy = newValue;
                const assistiveLabel = this.shadowRoot.getElementById(
                    'autocomplete-assistive-label'
                );

                if (assistiveLabel) {
                    assistiveLabel.textContent = newValue;
                }

                break;
            }
            case 'min-chars-text':
                this.settings.localization.minCharsHint = newValue;
                break;

            case 'max-items': {
                const maxItems = parseInt(newValue, 10);

                if (!isNaN(maxItems) && maxItems > 0) {
                    this.settings.maxItems = maxItems;
                }

                break;
            }
            case 'min-chars': {
                const minChars = parseInt(newValue, 10);
                if (!isNaN(minChars) && minChars > 0) {
                    this.settings.minChars = minChars;
                }
                break;
            }
            default:
                break;
        }
    }

    updateFloatingLabelClasses() {
        const label = this.shadowRoot.querySelector('label');
        const input = this.shadowRoot.querySelector('input');

        if (!label || !input) return;

        if (this.settings.label) {
            if (this.settings.placeholder === ' ') {
                if (label) {
                    label.textContent = this.settings.label;

                    if (!label.classList.contains('floating-label')) {
                        label.classList.add('floating-label');
                    }

                    label.classList.remove('hidden');
                }
            } else {
                if (input) {
                    input.setAttribute('aria-label', this.settings.label);

                    label.classList.remove('floating-label');

                    if (label) {
                        label.classList.add('hidden');
                    }
                }
            }
        }
    }

    addEventListeners() {
        const input = this.shadowRoot.querySelector('input');

        if (input) {
            input.addEventListener('input', (event) => this.handleInput(event));

            input.addEventListener('keydown', (event) =>
                this.handleInputKeyDown(event)
            );
        }

        const list = this.shadowRoot.querySelector('.autocomplete-list');

        if (list) {
            list.addEventListener('keydown', (event) => {
                this.handleListKeyDown(event);
            });
        }

        this.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget)) {
                const dropdown = this.shadowRoot.querySelector(
                    '.autocomplete-dropdown'
                );

                if (dropdown && !dropdown.classList.contains('hidden')) {
                    this.clearList();
                }

                const input = this.shadowRoot.querySelector('input');

                if (input) {
                    input.setAttribute('aria-expanded', 'false');
                }
            }
        });
    }

    get isDisabled() {
        return (
            this.hasAttribute('disabled') &&
            this.getAttribute('disabled') !== 'false'
        );
    }

    getInputValue() {
        const input = this.shadowRoot.querySelector('input');
        return input ? input.value : '';
    }

    initialize(settings) {
        this.settings = { ...this.settings, ...settings };

        if (this.settings.localization.describedBy) {
            const assistiveLabel = this.shadowRoot.getElementById(
                'autocomplete-assistive-label'
            );
            if (assistiveLabel) {
                assistiveLabel.textContent =
                    this.settings.localization.describedBy;
            }
        }
    }

    handleInput(event) {
        const value = event.target.value;

        if (this.inputTimer) {
            clearTimeout(this.inputTimer);
            this.inputTimer = null;
        }

        if (value.length >= this.settings.minChars) {
            this.filterOptions(value);
        } else {
            this.clearList();

            const remaining = this.settings.minChars - value.length;
            const shouldShowHint = value.length > 0 && remaining > 0;

            if (shouldShowHint) {
                this.inputTimer = setTimeout(() => {
                    if (this.settings.localization.minCharsHint) {
                        const hint = this.messageFormatter.format(
                            this.settings.localization.minCharsHint,
                            {
                                minCount: this.settings.minChars,
                                remaining,
                            }
                        );
                        this.updateLiveRegion(hint);
                    } else {
                        const plural =
                            remaining > 1 ? 'characters' : 'character';
                        const hint = `Type ${remaining} more ${plural} to see results`;

                        this.updateLiveRegion(hint);
                    }
                    this.inputTimer = null;
                }, this.settings.inputDelay || 300);
            }
        }
    }

    filterOptions(value) {
        if (typeof this.settings.customFilter === 'function') {
            this.settings.customFilter(
                value,
                this.settings.options,
                this.setFilterOptions.bind(this)
            );
        } else {
            this.filteredOptions = this.settings.options.filter((option) =>
                this.settings.filterFields.some((field) =>
                    option[field].toLowerCase().includes(value.toLowerCase())
                )
            );

            this.updateList();
        }
    }

    updateList() {
        const input = this.shadowRoot.querySelector('input');
        const dropdown = this.shadowRoot.querySelector(
            '.autocomplete-dropdown'
        );
        const list = this.shadowRoot.querySelector('.autocomplete-list');

        if (!input || !list) return;

        list.replaceChildren();

        if (this.filteredOptions.length === 0) {
            if (this.settings.showNoOptionsFound) {
                const noResultsItem = document.createElement('li');

                noResultsItem.classList.add('autocomplete-item', 'no-results');

                // Don't use render function for no results item. Implement here if needed.
                if (this.settings.localization.noResults) {
                    noResultsItem.textContent = this.messageFormatter.format(
                        this.settings.localization.noResults,
                        {
                            input: input.value,
                        }
                    );
                } else {
                    noResultsItem.textContent = 'No results found';
                }

                noResultsItem.setAttribute('aria-live', 'polite');
                noResultsItem.setAttribute('role', 'alert');
                noResultsItem.setAttribute('tabindex', '0');
                noResultsItem.style.pointerEvents = 'none';

                list.appendChild(noResultsItem);
                dropdown.classList.remove('hidden');

                const listFooter = this.shadowRoot.querySelector(
                    'slot[name="list-footer"]'
                );

                if (listFooter && !listFooter.classList.contains('hidden')) {
                    listFooter.classList.add('hidden');
                }
            }

            this.lastMessage = null;

            return;
        }

        this.filteredOptions
            .slice(0, this.settings.maxItems)
            .forEach((option, index) => {
                const item = document.createElement('li');

                if (typeof this.settings.render === 'function') {
                    const renderValue = this.settings.render(option);

                    if (renderValue instanceof HTMLElement) {
                        item.appendChild(renderValue);
                    } else if (typeof renderValue === 'string') {
                        item.innerHTML = renderValue;
                    }
                } else {
                    item.textContent =
                        option[this.settings.labelField ?? 'label'];
                }

                item.classList.add('autocomplete-item');
                item.setAttribute('id', `autocomplete-option-${index}`);
                item.setAttribute('role', 'option');
                item.setAttribute('tabindex', '0');
                item.setAttribute('part', 'item');
                item.setAttribute(
                    'aria-selected',
                    this.selectedOption &&
                        this.selectedOption.label === option.label &&
                        this.selectedOption.value === option.value
                        ? 'true'
                        : 'false'
                );
                item.setAttribute(
                    'data-value',
                    option[this.settings.valueField ?? 'value']
                );
                item.setAttribute('aria-posinset', index + 1);
                item.setAttribute(
                    'aria-setsize',
                    Math.min(
                        this.filteredOptions.length,
                        this.settings.maxItems
                    )
                );

                item.addEventListener('click', () => this.selectItem(option));
                list.appendChild(item);

                if (index === 0) {
                    item.setAttribute('tabindex', '0');
                } else {
                    item.setAttribute('tabindex', '-1');
                }
            });

        dropdown.classList.remove('hidden');

        const listFooter = this.shadowRoot.querySelector(
            'slot[name="list-footer"]'
        );

        if (listFooter) {
            listFooter.classList.remove('hidden');
        }

        input.setAttribute('aria-expanded', 'true');

        if (this.settings.localization.numResults) {
            const count = !isNaN(this.settings.maxItems)
                ? Math.min(this.filteredOptions.length, this.settings.maxItems)
                : this.filteredOptions.length;

            const message = this.messageFormatter.format(
                this.settings.localization.numResults,
                { count }
            );

            this.updateLiveRegion(message);
        } else {
            this.updateLiveRegion(
                `${
                    !isNaN(this.settings.maxItems)
                        ? Math.min(
                              this.filteredOptions.length,
                              this.settings.maxItems
                          )
                        : this.filteredOptions.length
                } results found`
            );
        }
    }

    clearList() {
        const dropdown = this.shadowRoot.querySelector(
            '.autocomplete-dropdown'
        );
        if (dropdown) {
            dropdown.classList.add('hidden');
        }

        const list = this.shadowRoot.querySelector('.autocomplete-list');
        if (list) {
            list.replaceChildren();
        }

        const input = this.shadowRoot.querySelector('input');
        if (input) {
            input.setAttribute('aria-expanded', 'false');
        }
    }

    selectItem(option) {
        this.selectedOption = option;

        const input = this.shadowRoot.querySelector('input');

        if (!input) return;

        input.value = option[this.settings.labelField ?? 'label'];
        this.clearList();

        if (typeof this.settings.onSelect === 'function') {
            this.settings.onSelect(option);
        }

        this.emit('select', { value: option });

        if (this.settings.localization.itemSelected) {
            const message = this.messageFormatter.format(
                this.settings.localization.itemSelected,
                {
                    option: option[this.settings.labelField ?? 'label'],
                }
            );
            this.updateLiveRegion(message);
        } else {
            this.updateLiveRegion(`${option.label} selected`);
        }
    }

    updateLiveRegion(message) {
        if (message === this.lastMessage) return;

        this.lastMessage = message;

        const liveRegion = this.shadowRoot.querySelector(
            '.autocomplete-live-region'
        );

        if (liveRegion) {
            liveRegion.textContent = message;

            setTimeout(() => (liveRegion.textContent = ''), 1000);
        }
    }

    emit(type, detail = {}) {
        let event = new CustomEvent(`autocomplete:${type}`, {
            bubbles: true,
            cancelable: true,
            detail: detail,
        });

        this.dispatchEvent(event);
    }

    handleInputKeyDown(event) {
        const list = this.shadowRoot.querySelector('.autocomplete-list');

        if (!list || list.classList.contains('hidden')) return;

        const items = Array.from(
            this.shadowRoot.querySelectorAll('.autocomplete-item')
        ).filter((item) => !item.classList.contains('no-results'));

        if (items.length === 0) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.navigateDown();
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateUp();
                break;
            case 'Enter': {
                event.preventDefault();
                const currentItem = this.shadowRoot.querySelector(
                    '.autocomplete-item:focus-visible'
                );
                if (currentItem) {
                    const value = currentItem.getAttribute('data-value');
                    const option = this.settings.options.find(
                        (opt) =>
                            opt[this.settings.valueField ?? 'value'] === value
                    );

                    if (option) {
                        this.selectItem(option);
                    }
                }
                break;
            }
            default:
                break;
        }
    }

    handleListKeyDown(event) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.navigateDown();
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateUp();
                break;
            case 'Escape':
                event.preventDefault();
                this.clearList();
                break;
            case 'Enter':
            case ' ': {
                event.preventDefault();
                const currentItem = this.shadowRoot.querySelector(
                    '.autocomplete-item:focus-visible'
                );
                if (currentItem) {
                    const value = currentItem.getAttribute('data-value');
                    const option = this.filteredOptions.find(
                        (opt) =>
                            opt[this.settings.valueField ?? 'value'] === value
                    );

                    if (option) {
                        this.selectItem(option);
                    }
                }
                break;
            }
            case 'Tab':
                this.handleListTabKey();
                break;
            default:
                break;
        }
    }

    navigateUp() {
        const list = this.shadowRoot.querySelector('.autocomplete-list');
        const input = this.shadowRoot.querySelector('input');

        if (!list || list.classList.contains('hidden')) return;

        const items = Array.from(
            this.shadowRoot.querySelectorAll('.autocomplete-item')
        ).filter((item) => !item.classList.contains('no-results'));

        if (items.length === 0) return;

        const currentIndex = items.findIndex(
            (item) => item == this.shadowRoot.activeElement
        );

        if (currentIndex === -1) return;

        if (currentIndex === 0) {
            if (input) {
                input.focus();
            }

            return;
        }

        const newIndex = (currentIndex - 1) % items.length;

        items[currentIndex].setAttribute('tabindex', '-1');
        items[newIndex].setAttribute('tabindex', '0');
        items[newIndex].focus();

        if (input) {
            input.setAttribute('aria-activedescendant', items[newIndex].id);
        }

        items[newIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
    }

    navigateDown() {
        const list = this.shadowRoot.querySelector('.autocomplete-list');
        const input = this.shadowRoot.querySelector('input');

        if (!list || list.classList.contains('hidden')) return;

        const items = Array.from(
            this.shadowRoot.querySelectorAll('.autocomplete-item')
        ).filter((item) => !item.classList.contains('no-results'));

        if (items.length === 0) return;

        let currentIndex = items.findIndex(
            (item) => item == this.shadowRoot.activeElement
        );

        if (currentIndex + 1 >= items.length) return;

        const newIndex = (currentIndex + 1 + items.length) % items.length;

        if (currentIndex >= 0) {
            items[currentIndex].setAttribute('tabindex', '-1');
        }

        items[newIndex].setAttribute('tabindex', '0');
        items[newIndex].focus();

        if (input) {
            input.setAttribute('aria-activedescendant', items[newIndex].id);
        }

        items[newIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
    }

    handleListTabKey() {
        const focusedItem = this.shadowRoot.querySelector(
            '.autocomplete-item:focus-visible'
        );

        if (focusedItem && this.settings.selectOnListTab) {
            focusedItem.click();
        }
    }

    setFilterOptions(options) {
        if (Array.isArray(options)) {
            const isArrayofStrings = options.every(
                (opt) => typeof opt === 'string'
            );
            if (isArrayofStrings) {
                this.filteredOptions = options.map((item) => ({
                    label: item,
                    value: item,
                }));
            } else {
                this.filteredOptions = options;
            }
        } else {
            console.warn('The options must be an array.');
        }

        this.updateList();
    }
}

customElements.define('svea-autocomplete', Autocomplete);
